import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DataTable,
  EmptyState,
  Input,
  Pill,
  SegmentedControl,
  Select,
  Sheet,
  Textarea,
  useToast,
} from "@astrum/ui";
import type { Column, PillTone } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { InfoPanelMember } from "./InfoPanel.js";
import "./project-log-tab.css";

/**
 * The project's risks, issues and decisions (RAID log): a filtered table with
 * a create/edit sheet. Everyone on the project reads; only people who can
 * manage the project see the write controls (the server enforces the same).
 */

export type LogKind = "risk" | "issue" | "decision";
type Level = "low" | "medium" | "high";

export type LogEntry = {
  id: string;
  kind: LogKind;
  title: string;
  description: string;
  ownerId: string | null;
  ownerName: string | null;
  status: string;
  probability: Level | null;
  impact: Level | null;
  severity: Level | null;
  dueDate: string | null;
  decidedAt: string | null;
  linkedTaskIds: string[];
};

const PAGE = 25;

const KIND_OPTIONS: { value: LogKind; label: string }[] = [
  { value: "risk", label: "Risks" },
  { value: "issue", label: "Issues" },
  { value: "decision", label: "Decisions" },
];
const KIND_SINGULAR: Record<LogKind, string> = { risk: "risk", issue: "issue", decision: "decision" };

const STATUSES: Record<LogKind, { value: string; label: string }[]> = {
  risk: [
    { value: "open", label: "Open" },
    { value: "mitigating", label: "Mitigating" },
    { value: "closed", label: "Closed" },
  ],
  issue: [
    { value: "open", label: "Open" },
    { value: "mitigating", label: "Mitigating" },
    { value: "closed", label: "Closed" },
  ],
  decision: [
    { value: "proposed", label: "Proposed" },
    { value: "decided", label: "Decided" },
    { value: "superseded", label: "Superseded" },
  ],
};

const STATUS_TONE: Record<string, PillTone> = {
  open: "danger",
  mitigating: "warn",
  closed: "neutral",
  proposed: "warn",
  decided: "ok",
  superseded: "neutral",
};
const SEVERITY_TONE: Record<Level, PillTone> = { low: "ok", medium: "warn", high: "danger" };
const LEVEL_LABEL: Record<Level, string> = { low: "Low", medium: "Medium", high: "High" };

function statusLabel(kind: LogKind, status: string): string {
  return STATUSES[kind].find((s) => s.value === status)?.label ?? status;
}

function formatDay(day: string | null): string {
  if (!day) return "—";
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/* ───────────────────────────── create / edit sheet ───────────────────────────── */

function LogSheet({
  projectId,
  kind,
  editing,
  members,
  onSaved,
  onClose,
}: {
  projectId: string;
  kind: LogKind;
  editing: LogEntry | null;
  members: InfoPanelMember[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [title, setTitle] = useState(editing?.title ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [ownerId, setOwnerId] = useState(editing?.ownerId ?? "");
  const [status, setStatus] = useState(editing?.status ?? STATUSES[kind][0]!.value);
  const [probability, setProbability] = useState<string>(editing?.probability ?? "");
  const [impact, setImpact] = useState<string>(editing?.impact ?? "");
  const [dueDate, setDueDate] = useState(editing?.dueDate ?? "");
  const [saving, setSaving] = useState(false);

  const submit = () => {
    if (!title.trim()) return;
    setSaving(true);
    const body = {
      title,
      description,
      ownerId: ownerId || null,
      status,
      dueDate: dueDate || null,
      ...(kind === "risk" ? { probability: probability || null, impact: impact || null } : {}),
    };
    const request = editing
      ? api.patch(`/projects/${projectId}/log/${editing.id}`, body)
      : api.post(`/projects/${projectId}/log`, { kind, ...body });
    request
      .then(() => {
        toast(editing ? "Entry saved." : `${KIND_SINGULAR[kind][0]!.toUpperCase()}${KIND_SINGULAR[kind].slice(1)} added.`);
        onSaved();
        onClose();
      })
      .catch((error: unknown) =>
        toast(error instanceof ApiError ? error.message : "Could not save the entry.", { tone: "danger" }),
      )
      .finally(() => setSaving(false));
  };

  return (
    <Sheet open onClose={onClose} title={`${editing ? "Edit" : "New"} ${KIND_SINGULAR[kind]}`}>
      <form
        className="log-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input label="Title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} autoFocus required />
        <Textarea label="Description" value={description} maxRows={8} onChange={(e) => setDescription(e.target.value)} />
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES[kind].map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
        {kind === "risk" && (
          <div className="log-form__row">
            <Select label="Probability" value={probability} onChange={(e) => setProbability(e.target.value)}>
              <option value="">Not assessed</option>
              {(["low", "medium", "high"] as Level[]).map((l) => (
                <option key={l} value={l}>
                  {LEVEL_LABEL[l]}
                </option>
              ))}
            </Select>
            <Select label="Impact" value={impact} onChange={(e) => setImpact(e.target.value)}>
              <option value="">Not assessed</option>
              {(["low", "medium", "high"] as Level[]).map((l) => (
                <option key={l} value={l}>
                  {LEVEL_LABEL[l]}
                </option>
              ))}
            </Select>
          </div>
        )}
        <Select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
          <option value="">No owner</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.displayName}
            </option>
          ))}
        </Select>
        <Input
          label={kind === "decision" ? "Review date" : "Due date"}
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        <div className="log-form__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={!title.trim()}>
            {editing ? "Save" : "Add"}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ───────────────────────────────── the tab ───────────────────────────────── */

export function ProjectLogTab({
  projectId,
  members,
  canEdit,
  onChanged,
}: {
  projectId: string;
  members: InfoPanelMember[];
  canEdit: boolean;
  /** Called after any write, so the project's summary counts can refresh. */
  onChanged?: () => void;
}) {
  const { toast } = useToast();
  const [kind, setKind] = useState<LogKind>("risk");
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState<{ editing: LogEntry | null } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ entries: LogEntry[]; total: number }>(`/projects/${projectId}/log?kind=${kind}&limit=${PAGE}&offset=${offset}`)
      .then((res) => {
        setEntries(res.entries);
        setTotal(res.total);
      })
      .catch(() => toast("Could not load the log.", { tone: "danger" }))
      .finally(() => setLoading(false));
  }, [projectId, kind, offset, toast]);

  useEffect(load, [load]);

  const saved = () => {
    load();
    onChanged?.();
  };

  const remove = (entry: LogEntry) => {
    if (!window.confirm(`Delete "${entry.title}"?`)) return;
    api
      .delete(`/projects/${projectId}/log/${entry.id}`)
      .then(saved)
      .catch(() => toast("Could not delete the entry.", { tone: "danger" }));
  };

  const columns: Column<LogEntry>[] = [
    { key: "title", header: "Title", rowHeader: true, render: (e) => e.title },
    ...(kind === "risk"
      ? [
          {
            key: "severity",
            header: "Severity",
            render: (e: LogEntry) =>
              e.severity ? <Pill tone={SEVERITY_TONE[e.severity]}>{LEVEL_LABEL[e.severity]}</Pill> : "—",
          },
        ]
      : []),
    {
      key: "status",
      header: "Status",
      render: (e) => <Pill tone={STATUS_TONE[e.status] ?? "neutral"}>{statusLabel(e.kind, e.status)}</Pill>,
    },
    { key: "owner", header: "Owner", render: (e) => e.ownerName ?? "—" },
    {
      key: "due",
      header: kind === "decision" ? "Review" : "Due",
      render: (e) => formatDay(e.dueDate),
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            header: "",
            align: "end" as const,
            render: (e: LogEntry) => (
              <span className="log-tab__row-actions">
                <Button size="sm" variant="ghost" onClick={() => setSheet({ editing: e })}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(e)}>
                  Delete
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="log-tab">
      <div className="log-tab__bar">
        <SegmentedControl
          aria-label="Log kind"
          options={KIND_OPTIONS}
          value={kind}
          onChange={(next) => {
            setKind(next);
            setOffset(0);
          }}
        />
        {canEdit && <Button onClick={() => setSheet({ editing: null })}>Add {KIND_SINGULAR[kind]}</Button>}
      </div>

      <DataTable
        caption={`Project ${KIND_OPTIONS.find((o) => o.value === kind)!.label.toLowerCase()}`}
        columns={columns}
        rows={entries}
        rowKey={(e) => e.id}
        loading={loading}
        onRowClick={canEdit ? (e) => setSheet({ editing: e }) : undefined}
        empty={
          <EmptyState
            title={`No ${KIND_OPTIONS.find((o) => o.value === kind)!.label.toLowerCase()} logged`}
            description={canEdit ? `Add one to start tracking.` : undefined}
          />
        }
      />

      {total > PAGE && (
        <div className="log-tab__pager">
          <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Previous
          </Button>
          <span>
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <Button size="sm" variant="secondary" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            Next
          </Button>
        </div>
      )}

      {sheet && (
        <LogSheet
          projectId={projectId}
          kind={kind}
          editing={sheet.editing}
          members={members}
          onSaved={saved}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}
